/**
 * Custom Search Implementation
 * - Handles Overlay Toggling
 * - Predictive Search API Integration
 * - Debouncing
 */

class SearchOverlay {
    constructor() {
        this.overlay = document.getElementById('SearchOverlay');
        this.input = document.getElementById('SearchOverlayInput');
        this.resultsContainer = document.querySelector('[data-predictive-search-results]');
        this.resultsList = document.querySelector('[data-search-results-list]');
        this.spinner = document.querySelector('[data-search-spinner]');
        this.emptyState = document.querySelector('[data-search-empty]');
        this.viewAll = document.querySelector('[data-search-view-all]');

        this.isOpen = false;
        this.controller = null; // for aborting fetch

        this.init();
    }

    init() {
        if (!this.overlay) return;

        // Toggles
        document.querySelectorAll('[data-search-trigger]').forEach(trigger => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.open();
            });
        });

        document.querySelectorAll('[data-search-close]').forEach(close => {
            close.addEventListener('click', () => this.close());
        });

        // Close on Escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) this.close();
        });

        // Input Handling
        this.input.addEventListener('input', this.debounce((e) => {
            this.onChange(e.target.value);
        }, 300));
    }

    open() {
        this.isOpen = true;
        this.overlay.setAttribute('aria-hidden', 'false');
        document.body.classList.add('search-overlay-open');
        this.input.focus();
        // Trap focus logic could be added here
    }

    close() {
        this.isOpen = false;
        this.overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('search-overlay-open');
        // Return focus to trigger logic could be added here
    }

    debounce(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    }

    onChange(searchTerm) {
        const term = searchTerm.trim();

        if (!term.length) {
            this.resultsList.innerHTML = '';
            this.emptyState.classList.add('hidden');
            this.viewAll.classList.add('hidden');
            return;
        }

        this.getSearchResults(term);
    }

    getSearchResults(term) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();

        this.spinner.classList.remove('hidden');
        this.emptyState.classList.add('hidden');
        // Keep stale results until new ones arrive? No, maybe clear
        // this.resultsList.innerHTML = ''; 

        fetch(`/search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6`, {
            signal: this.controller.signal
        })
            .then((response) => {
                if (!response.ok) {
                    var error = new Error(response.statusText);
                    error.code = response.status;
                    throw error;
                }
                return response.json();
            })
            .then((suggestions) => {
                const products = suggestions.resources.results.products;
                this.renderResults(products, term);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error('Search error:', error);
                    this.resultsList.innerHTML = '<p>Something went wrong. Please try again.</p>';
                }
            })
            .finally(() => {
                this.spinner.classList.add('hidden');
            });
    }

    renderResults(products, term) {
        this.resultsList.innerHTML = '';

        if (!products || products.length === 0) {
            this.emptyState.classList.remove('hidden');
            this.viewAll.classList.add('hidden');
            return;
        }

        this.emptyState.classList.add('hidden');
        this.viewAll.classList.remove('hidden');

        products.forEach(product => {
            const item = document.createElement('div');
            item.className = 'search-overlay__result-item';

            const price = product.available ? this.formatMoney(product.price) : 'Sold Out';
            const comparePrice = product.compare_at_price > product.price ?
                `<span class="line-through text-gray-500 text-xs ml-2">${this.formatMoney(product.compare_at_price)}</span>` : '';

            item.innerHTML = `
        <a href="${product.url}" class="block group">
           <div class="aspect-square w-full overflow-hidden rounded bg-gray-100 mb-2">
             <img src="${product.image || ''}" alt="${product.title}" width="200" height="200" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300">
           </div>
           <h3 class="search-overlay__result-title group-hover:underline">${this.highlightTerm(product.title, term)}</h3>
           <div class="search-overlay__result-price mt-1">
             <span>${price}</span>${comparePrice}
           </div>
        </a>
      `;
            this.resultsList.appendChild(item);
        });
    }

    highlightTerm(text, term) {
        if (!term) return text;
        const regex = new RegExp(`(${term})`, 'gi');
        return text.replace(regex, '<mark>$1</mark>');
    }

    formatMoney(cents) {
        // Simple formatter, can be replaced with Shopify.formatMoney if available
        return (cents / 100).toLocaleString('en-GB', {
            style: 'currency',
            currency: 'GBP'
        });
    }
}

// Initialize on DOM Ready
document.addEventListener('DOMContentLoaded', () => {
    new SearchOverlay();
});
